import { useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValueEvent, useReducedMotion, useScroll } from "framer-motion";
import SplitFlap from "@/components/site/SplitFlap";
import StreakIcon from "@/components/app/StreakIcon";

const DAY_START = 6 * 60;
const DAY_END = 24 * 60 - 1;
const SPAN = DAY_END - DAY_START;

// One illustrative Thursday. Every beat maps to something the workspace actually does.
const EVENTS = [
  { at: 360, kind: "checkin", title: "Morning check-in. Both in.", body: "You: GATE prep, three mock sections. Aanya: ship onboarding v2." },
  { at: 450, kind: "focus", title: "Focus room. Both desks lit.", body: "Ninety minutes, timers in sync. You can see she's still there." },
  { at: 670, kind: "verify", title: "Aanya verified your task.", body: "Mock section 1, 62/100. Marked done, witnessed." },
  { at: 940, kind: "quiet", title: "You went quiet.", body: "Nothing since lunch. Your partner noticed before you did." },
  { at: 960, kind: "nudge", title: "Nudge from Aanya.", body: "“Section 2. You said before five.”" },
  { at: 1015, kind: "done", title: "Section 2, done.", body: "Nudge answered in 55 minutes. She sees it land." },
  { at: 1350, kind: "checkin", title: "Evening check-in.", body: "You 5.5h, Aanya 6h. Wins logged, tomorrow planned." },
  { at: 1439, kind: "streak", title: "Day closed. Streak 44 → 45.", body: "Neither of you skipped. That's the whole trick." },
];

const LANES = [
  { who: "You", blocks: [[450, 540], [600, 700], [985, 1040], [1260, 1330]] },
  { who: "Aanya", blocks: [[450, 540], [620, 760], [900, 1080], [1200, 1320]] },
];

const MARKERS = [6, 9, 12, 15, 18, 21, 24];

const pct = (m) => ((m - DAY_START) / SPAN) * 100;
const fmt = (m) => {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

// The room's light follows the clock: first light, flat noon, amber dusk, dark.
const LIGHT = [
  [360, [245, 158, 11, 0.22]],
  [540, [245, 190, 110, 0.12]],
  [780, [245, 245, 240, 0.06]],
  [1080, [217, 119, 6, 0.24]],
  [1260, [180, 83, 9, 0.12]],
  [1439, [120, 60, 10, 0.03]],
];
function dayLight(m) {
  let i = LIGHT.findIndex(([at]) => at >= m);
  if (i <= 0) i = 1;
  const [a0, c0] = LIGHT[i - 1];
  const [a1, c1] = LIGHT[i];
  const t = Math.min(1, Math.max(0, (m - a0) / (a1 - a0)));
  const c = c0.map((v, k) => v + (c1[k] - v) * t);
  const d = (m - DAY_START) / SPAN;
  return {
    "--gc": `rgba(${c[0] | 0}, ${c[1] | 0}, ${c[2] | 0}, ${c[3].toFixed(3)})`,
    "--gx": `${12 + d * 76}%`,
    "--gy": `${95 - Math.sin(d * Math.PI) * 70}%`,
  };
}

export default function PairedDay() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [now, setNow] = useState(DAY_START);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  useMotionValueEvent(scrollYProgress, "change", (p) => {
    const raw = DAY_START + p * SPAN;
    const next = p > 0.985 ? DAY_END : Math.floor(raw / 5) * 5;
    setNow((prev) => (prev === next ? prev : next));
  });

  const seen = EVENTS.filter((e) => e.at <= now);
  const recent = seen.slice(-3);
  const streak = now >= DAY_END ? "45" : "44";

  return (
    <section id="day" data-testid="day-section" className="relative border-y border-white/5 bg-[#080808]">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-28 sm:pt-36">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <h2 className="lg:col-span-7 font-serif-editorial font-light tracking-tight leading-[1.02] text-4xl sm:text-6xl text-balance">
            One day, <em className="italic text-[#F59E0B]">paired.</em>
          </h2>
          <p className="lg:col-span-5 text-[#A1A1AA] font-light leading-relaxed max-w-md">
            Scroll the clock. This is the whole product in one day, from the first check-in to the streak tick.
            <span className="block mt-2 text-xs text-[#7c7c84]">Illustrative day. Names and tasks are examples.</span>
          </p>
        </div>
      </div>

      <div ref={ref} className="relative h-[460vh]">
        <div className="sticky top-0 h-[100svh] flex items-center overflow-hidden">
          <div className="day-glow" style={dayLight(now)} aria-hidden="true" />
          <div className="relative w-full max-w-6xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
            {/* Clock + lanes */}
            <div className="lg:col-span-6">
              <div className="flex items-end gap-5">
                <SplitFlap value={fmt(now)} className="flap-lg" label={`Time ${fmt(now)}`} />
              </div>
              <div className="mt-4 flex items-baseline gap-3 text-sm">
                <span className="font-serif-editorial italic text-3xl text-[#F5F5F0]">Thursday</span>
                <span className="text-[#7c7c84]">·</span>
                <span className="inline-flex items-baseline gap-1.5 text-[#A1A1AA] tabular-nums"><StreakIcon className="h-3.5 w-3.5 self-center text-[#F59E0B]" /> shared streak <span className="text-[#F59E0B]">{streak}</span> days</span>
              </div>

              <div className="mt-10 sm:mt-12" aria-hidden="true">
                <div className="relative">
                  {LANES.map((lane) => (
                    <div key={lane.who} className="grid grid-cols-[3.75rem_1fr] items-center gap-3 mb-3">
                      <span className="text-xs text-[#A1A1AA]">{lane.who}</span>
                      <div className="relative h-7 rounded-[3px] bg-white/[0.03] border border-white/[0.06] overflow-hidden">
                        {lane.blocks.map(([s, e]) => {
                          const visibleEnd = Math.min(e, now);
                          if (visibleEnd <= s) return null;
                          const live = now > s && now < e;
                          return (
                            <span
                              key={s}
                              className={`absolute inset-y-1 rounded-[2px] ${live ? "bg-[#F59E0B] lane-live" : "bg-[#F59E0B]/55"}`}
                              style={{ left: `${pct(s)}%`, width: `${pct(visibleEnd) - pct(s)}%` }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <div className="grid grid-cols-[3.75rem_1fr] gap-3">
                    <span />
                    <div className="relative h-5">
                      {MARKERS.map((h) => (
                        <span key={h} className="absolute -translate-x-1/2 text-[11px] text-[#8a8a92] tabular-nums" style={{ left: `${pct(Math.min(h * 60, DAY_END))}%` }}>
                          {String(h % 24).padStart(2, "0")}
                        </span>
                      ))}
                    </div>
                  </div>
                  {/* now line */}
                  <div className="pointer-events-none absolute inset-y-0 left-[calc(3.75rem+0.75rem)] right-0 -top-2 bottom-5">
                    <span className="absolute top-0 bottom-0 w-px bg-[#F5F5F0]/70" style={{ left: `${pct(now)}%` }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Event feed */}
            <div className="lg:col-span-6 relative min-h-[15rem] sm:min-h-[19rem]" aria-hidden="true">
              <ol className="flex flex-col gap-3">
                <AnimatePresence initial={false} mode="popLayout">
                  {recent.map((e, i) => {
                    const latest = i === recent.length - 1;
                    return (
                      <motion.li
                        key={e.at}
                        layout={!reduce}
                        initial={reduce ? false : { opacity: 0, y: 24, filter: "blur(6px)" }}
                        animate={{ opacity: latest ? 1 : 0.42, y: 0, filter: "blur(0px)" }}
                        exit={reduce ? undefined : { opacity: 0, y: -16, filter: "blur(4px)" }}
                        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                        className={`day-event day-event--${e.kind} ${latest ? "is-latest" : ""} ${i < recent.length - 2 ? "hidden sm:grid" : "grid"}`}
                      >
                        <span className="text-xs tabular-nums text-[#A1A1AA] pt-1.5">{fmt(e.at)}</span>
                        <div>
                          <div className="font-serif-editorial text-2xl sm:text-[1.7rem] leading-tight text-[#F5F5F0]">{e.title}</div>
                          <p className="mt-1 text-sm text-[#A1A1AA] font-light leading-relaxed">{e.body}</p>
                        </div>
                      </motion.li>
                    );
                  })}
                </AnimatePresence>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Screen-reader version of the scroll story */}
      <ol className="sr-only">
        {EVENTS.map((e) => <li key={e.at}>{fmt(e.at)}: {e.title} {e.body}</li>)}
      </ol>
    </section>
  );
}
